import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History, MessageSquare, FileClock } from 'lucide-react';
import { QueryState, StatusPill, formatDateTime } from '@/components/live';
import { callRpc, describeError } from '@/api/resources';
import { toast } from '@/lib/toast';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import type { AuditRow } from '@/types/records';

/** Friendlier names for the tables a record's history spans. */
const TABLE_LABELS: Record<string, string> = {
  'people.farmers': 'Farmer',
  'party.parties': 'Identity & contact',
  'ops.farm_plots': 'Plot',
  'geo.plot_geometries': 'Boundary',
  'party.party_kyc_records': 'KYC',
};

/**
 * One record's history, from the append-only audit log (owner, 2026-09-21: "add record history
 * tab"). Read through the same `api.audit_timeline` view as the Audit screen, so it shows what
 * the caller's role may read and nothing more, with sensitive values already redacted at write
 * time. A record is often several rows - a farmer is the farmer row and the party that holds
 * their contacts - so it takes a list of ids.
 *
 * What is shown is deliberately simple for now; which events and fields to show per record type
 * is expected to become configuration later.
 */
interface CommentRow { id: string; entity_id: string; comment: string; created_at: string; author_name: string | null }

/** A version kept when an approved record was amended (0088). */
interface RevisionRow {
  revision_no: number; effective_from: string | null; replaced_at: string; replaced_by: string | null;
  change_reason: string | null; snapshot: Record<string, unknown>;
}

/** Bookkeeping the reader does not need to see in an old version. */
const SNAPSHOT_SKIP = new Set([
  'id', 'organization_id', 'created_by', 'created_at', 'updated_by', 'updated_at', 'submitted_by', 'submitted_at',
  'approved_by', 'approved_at', 'is_deleted', 'deleted_by', 'deleted_at', 'delete_reason', 'version_no',
  'is_current_version', 'source_record_id', 'search_tsv',
]);

/** The record as it stood, as readable "field: value" lines. */
export function snapshotLines(snapshot: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(snapshot ?? {})
    .filter(([key, value]) => !SNAPSHOT_SKIP.has(key) && value !== null && value !== '' && !key.endsWith('_id'))
    .map(([key, value]) => [
      key.replace(/_/g, ' ').replace(/^./, (first) => first.toUpperCase()),
      typeof value === 'object' ? JSON.stringify(value) : String(value),
    ] as [string, string])
    .sort((a, b) => a[0].localeCompare(b[0]));
}

type Line =
  | { kind: 'event'; at: string; event: AuditRow }
  | { kind: 'comment'; at: string; comment: CommentRow }
  | { kind: 'version'; at: string; revision: RevisionRow };

export function RecordHistory({
  entityIds,
  commentOn,
}: {
  entityIds: Array<string | null | undefined>;
  /** Where a new comment is filed (owner, 2026-09-21: "comment field in audit history"). */
  commentOn?: { table: string; id: string };
}) {
  const ids = [...new Set(entityIds.filter((id): id is string => Boolean(id)))];
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const comments = useQuery({
    queryKey: ['record-comments', ids],
    enabled: isSupabaseConfigured && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await getSupabase().from('record_comments' as never)
        .select('*').in('entity_id', ids as never).order('created_at', { ascending: false }).limit(200);
      if (error) throw new Error(describeError(error));
      return (data ?? []) as CommentRow[];
    },
  });

  async function post() {
    if (!commentOn || !draft.trim()) return;
    setPosting(true);
    try {
      await callRpc('fn_add_record_comment', {
        p_entity_table: commentOn.table, p_entity_id: commentOn.id, p_comment: draft.trim(),
      });
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: ['record-comments'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The comment could not be added.');
    } finally {
      setPosting(false);
    }
  }
  const events = useQuery({
    queryKey: ['record-history', ids],
    enabled: isSupabaseConfigured && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await getSupabase().from('audit_timeline' as never)
        .select('*').in('entity_id', ids as never).order('event_at', { ascending: false }).limit(200);
      if (error) throw new Error(describeError(error));
      return (data ?? []) as AuditRow[];
    },
  });

  // The versions kept when this record was amended after approval (0088). Only five record types
  // keep versions; for anything else the call returns nothing.
  const revisions = useQuery({
    queryKey: ['record-revisions', commentOn?.table, commentOn?.id],
    enabled: isSupabaseConfigured && Boolean(commentOn),
    queryFn: () => callRpc<RevisionRow[]>('fn_record_revisions', {
      p_entity_table: commentOn!.table, p_entity_id: commentOn!.id,
    }),
  });

  // Events, comments and kept versions, one timeline, newest first.
  const lines: Line[] = [
    ...(events.data ?? []).map((event) => ({ kind: 'event' as const, at: event.event_at, event })),
    ...(comments.data ?? []).map((comment) => ({ kind: 'comment' as const, at: comment.created_at, comment })),
    ...(revisions.data ?? []).map((revision) => ({ kind: 'version' as const, at: revision.replaced_at, revision })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <div className="record-history">
      {commentOn && (
        <div className="history-comment-box">
          <textarea aria-label="Add a comment" maxLength={2000} onChange={(event) => setDraft(event.target.value)}
                    placeholder="Add a comment to this record's history..." rows={2} value={draft} />
          <button className="primary" disabled={posting || !draft.trim()} onClick={() => void post()} type="button">
            <MessageSquare size={15} /> {posting ? 'Adding...' : 'Comment'}
          </button>
          <small className="muted">Comments are kept for good - they cannot be edited or deleted.</small>
        </div>
      )}
    <QueryState empty="No history recorded yet, or your role cannot read this record's history." query={events}>
      <ol className="audit-timeline">
        {lines.map((line) => line.kind === 'version' ? (
          <li className="history-version" key={`v-${line.revision.revision_no}`}>
            <div className="audit-mark"><FileClock size={13} /></div>
            <div className="audit-body">
              <p>Version {line.revision.revision_no} was kept when this record was amended.</p>
              {line.revision.change_reason && <p className="muted">Reason: {line.revision.change_reason}</p>}
              <details>
                <summary>What it held</summary>
                <dl className="version-snapshot">
                  {snapshotLines(line.revision.snapshot).map(([label, value]) => (
                    <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                  ))}
                </dl>
              </details>
              <div className="audit-meta">
                <span>{formatDateTime(line.revision.replaced_at)}</span>
                <span>{line.revision.replaced_by ?? 'A user'}</span>
                <span className="muted">Version {line.revision.revision_no}</span>
              </div>
            </div>
          </li>
        ) : line.kind === 'comment' ? (
          <li className="history-comment" key={`c-${line.comment.id}`}>
            <div className="audit-mark"><MessageSquare size={13} /></div>
            <div className="audit-body">
              <p>{line.comment.comment}</p>
              <div className="audit-meta">
                <span>{formatDateTime(line.comment.created_at)}</span>
                <span>{line.comment.author_name ?? 'A user'}</span>
                <span className="muted">Comment</span>
              </div>
            </div>
          </li>
        ) : (() => { const event = line.event; return (
          <li key={event.id}>
            <div className="audit-mark"><History size={13} /></div>
            <div className="audit-body">
              <p>{event.natural_language_summary ?? `${event.event_verb ?? event.event_type} ${event.entity_label ?? ''}`}</p>
              <div className="audit-meta">
                <span>{formatDateTime(event.event_at)}</span>
                {event.actor_label && <span>{event.actor_label}</span>}
                <span className="muted">{TABLE_LABELS[event.entity_table] ?? event.entity_table}</span>
                {event.risk_level && event.risk_level !== 'low' && <StatusPill value={event.risk_level} />}
              </div>
            </div>
          </li>
        ); })())}
      </ol>
    </QueryState>
    </div>
  );
}
